/**
 * Workflow Status Panel
 * Displays workflow execution status, progress, and results
 * Extracted from SimplifiedWorkflow.js for better organization
 */
import React, { useState } from 'react';
import { Card, ProgressBar, Alert, Button, Modal, Table, Badge } from 'react-bootstrap';
import { useI18n } from '../../i18n';
import { FaDownload, FaEye, FaPlay, FaStop, FaCheck, FaTimes } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { workflowAPI } from '../../utils/httpClient';

const WorkflowStatusPanel = ({
    jobStatus,
    jobProgress,
    jobDescription,
    jobError,
    isPolling,
    testResultData,
    showTestResult,
    setShowTestResult,
    isTestMode,
    selectedRow,
    setSelectedRow,
    hideInfo = false,
    jobId = null
}) => {
    const [showProgressDetails, setShowProgressDetails] = useState(false);
    const [actualResultData, setActualResultData] = useState(null);
    const [loadingResults, setLoadingResults] = useState(false);
    const { t } = useI18n();

    // Get status variant for styling
    const getStatusVariant = (status) => {
        switch (status) {
            case 'SUCCESS': return 'success';
            case 'FAILED': return 'danger';
            case 'PROCESSING': return 'info';
            case 'PENDING': return 'warning';
            default: return 'secondary';
        }
    };

    // Get progress variant for progress bar
    const getProgressVariant = () => {
        if (jobError) return 'danger';
        if (jobStatus === 'SUCCESS') return 'success';
        if (jobStatus === 'PROCESSING') return 'info';
        return 'warning';
    };

    // Fetch actual workflow results from backend
    const fetchWorkflowResults = async () => {
        if (!jobId) {
            console.error('No job ID available for fetching results');
            return;
        }

        setLoadingResults(true);
        try {
            const resultData = await workflowAPI.getWorkflowResults(jobId);
            console.log('🔍 Debug: Workflow results from backend:', resultData);

            if (resultData.success) {
                console.log('🔍 Debug: Result data structure:', resultData.data);

                // Use backend data as-is; do not inject mock summary
                const enrichedData = { ...resultData.data };
                setActualResultData(enrichedData);
                setShowTestResult(true);
            } else {
                console.error('Failed to fetch workflow results:', resultData.message);
                // Fallback to testResultData if available
                console.log('🔍 Debug: Fallback to testResultData:', testResultData);
                setActualResultData(testResultData);
                setShowTestResult(true);
            }
        } catch (error) {
            console.error('Error fetching workflow results:', error);
            // Fallback to testResultData if available
            console.log('🔍 Debug: Error fallback to testResultData:', testResultData);
            setActualResultData(testResultData);
            setShowTestResult(true);
        } finally {
            setLoadingResults(false);
        }
    };

    // Download results - now supports ZIP files from backend
    const downloadResults = async () => {
        try {
            if (!jobId) {
                console.warn('No jobId available for download');
                return;
            }

            // Always request fresh presigned URLs from backend at click time
            const resultData = await workflowAPI.getWorkflowResults(jobId);
            if (resultData.success && resultData.data && resultData.data.download_urls) {
                const urls = resultData.data.download_urls;
                let downloadUrl = urls.primary_download || urls.workflow_results_package;
                if (!downloadUrl) {
                    const zipKey = Object.keys(urls).find(k => String(k).toLowerCase().endsWith('.zip') || String(k).includes('workflow_results_package'));
                    if (zipKey) downloadUrl = urls[zipKey];
                }
                if (!downloadUrl) {
                    const firstKey = Object.keys(urls)[0];
                    if (firstKey) downloadUrl = urls[firstKey];
                }
                if (downloadUrl) {
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.download = `workflow_results_${new Date().toISOString().replace(/[:.]/g, '-')}`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    return;
                }
            }

            // Legacy fallback: Only attempt Excel if we actually have data in cache
            if (testResultData && (testResultData.results || testResultData.summary)) {
                const workbook = XLSX.utils.book_new();
                if (testResultData.results) {
                    const worksheet = XLSX.utils.json_to_sheet(testResultData.results);
                    XLSX.utils.book_append_sheet(workbook, worksheet, 'Translation Results');
                }
                if (testResultData.summary) {
                    const summarySheet = XLSX.utils.json_to_sheet([testResultData.summary]);
                    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
                }
                if (workbook.SheetNames.length > 0) {
                    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                    const filename = `workflow_results_${timestamp}.xlsx`;
                    XLSX.writeFile(workbook, filename);
                    return;
                }
            }

            console.warn('Results are not ready to download yet. Please try again shortly.');
        } catch (error) {
            console.error('Error downloading results:', error);
        }
    };

    return (
        <div className="workflow-status-panel">
            {/* Execution Status */}
            <Card className="mb-3">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h6>{t('workflow:status.executionStatus')}</h6>
                    {isTestMode && (<Badge bg="warning">{t('workflow:status.testMode')}</Badge>)}
                </Card.Header>
                <Card.Body>
                    {jobStatus ? (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span>{t('workflow:status.status')}</span>
                                <Badge bg={getStatusVariant(jobStatus)}>
                                    {isPolling && <FaPlay className="me-1" />}
                                    {jobStatus === 'SUCCESS' && <FaCheck className="me-1" />}
                                    {jobStatus === 'FAILED' && <FaTimes className="me-1" />}
                                    {jobStatus}
                                </Badge>
                            </div>

                            {jobProgress > 0 && (
                                <div className="mb-2">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <small>{t('workflow:status.progress')}</small>
                                        <small>{Math.round(jobProgress)}%</small>
                                    </div>
                                    <ProgressBar
                                        now={jobProgress}
                                        variant={getProgressVariant()}
                                        animated={isPolling}
                                    />
                                </div>
                            )}

                            {jobDescription && (
                                <div className="mb-2">
                                    <small className="text-muted">{jobDescription}</small>
                                </div>
                            )}

                            {jobError && (
                                <Alert variant="danger" className="mb-2">
                                    <Alert.Heading>{t('workflow:status.error')}</Alert.Heading>
                                    <p className="mb-0">{jobError}</p>
                                </Alert>
                            )}

                            {jobStatus === 'SUCCESS' && (
                                <div>
                                    {/* 操作按钮（总是可见，允许用户主动获取结果）*/}
                                    <div className="d-grid gap-2">
                                        <Button
                                            variant="outline-primary"
                                            onClick={fetchWorkflowResults}
                                            disabled={loadingResults}
                                        >
                                            <FaEye className="me-2" />
                                            {loadingResults ? t('workflow:status.loadingResults') : t('workflow:status.viewResults')}
                                        </Button>
                                        <Button
                                            variant="outline-success"
                                            onClick={downloadResults}
                                        >
                                            <FaDownload className="me-2" />
                                            {t('workflow:status.downloadResults')}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <p className="text-muted mb-0">{t('workflow:status.noActiveWorkflow')}</p>
                    )}
                </Card.Body>
            </Card>

            {/* Test Mode Controls */}
            {isTestMode && (
                <Card className="mb-3">
                    <Card.Header>
                        <h6>{t('workflow:status.testModeSettings')}</h6>
                    </Card.Header>
                    <Card.Body>
                        <div className="mb-3">
                            <label className="form-label">{t('workflow:status.testRowIndex')}</label>
                            <input
                                type="number"
                                className="form-control"
                                value={selectedRow || ''}
                                onChange={(e) => setSelectedRow(parseInt(e.target.value) || null)}
                                placeholder={t('workflow:status.testRowPlaceholder')}
                                min="0"
                            />
                            <div className="form-text">
                                {t('workflow:status.testRowHint')}
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {!hideInfo && (
                <Card>
                    <Card.Header>
                        <h6>{t('workflow:status.info')}</h6>
                    </Card.Header>
                    <Card.Body>
                        <small className="text-muted">
                            <div>Enhanced workflow system with:</div>
                            <ul className="mb-0 mt-1">
                                <li>{t('workflow:status.infoList.realtime')}</li>
                                <li>{t('workflow:status.infoList.errorContext')}</li>
                                <li>{t('workflow:status.infoList.test')}</li>
                                <li>{t('workflow:status.infoList.cloud')}</li>
                                <li>{t('workflow:status.infoList.prompt')}</li>
                            </ul>
                        </small>
                    </Card.Body>
                </Card>
            )}

            {/* Results Modal */}
            <Modal 
                show={showTestResult} 
                onHide={() => setShowTestResult(false)}
                size="xl"
                scrollable
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('workflow:status.resultsTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingResults ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary mb-3" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p>{t('workflow:status.loadingResults')}</p>
                        </div>
                    ) : actualResultData || testResultData ? (
                        <div>
                            {/* Use actualResultData if available, otherwise fall back to testResultData */}
                            {(() => {
                                const resultData = actualResultData || testResultData;

                                return (
                                    <>
                                        {/* Data Source Info */}
                                        <div className="alert alert-info mb-3">
                                            <small>
                                                {t('workflow:status.dataSource')}: {actualResultData ? t('workflow:status.backendAPI') : t('workflow:status.executionCache')}
                                                {jobId && <span className="ms-2">| {t('workflow:status.jobId')}: {jobId}</span>}
                                            </small>
                                        </div>

                                        {/* Execution Summary (New Format) */}
                                        {resultData.execution_summary && (
                                            <Alert variant="success" className="mb-3">
                                                <Alert.Heading>{t('workflow:status.executionSummary')}</Alert.Heading>
                                                <div className="row">
                                                    <div className="col-md-6 mb-2">
                                                        <strong>{t('workflow:status.totalModules')}:</strong> {resultData.execution_summary.total_modules || 0}
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>{t('workflow:status.successfulModules')}:</strong> <Badge bg="success">{resultData.execution_summary.successful_modules || 0}</Badge>
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>{t('workflow:status.failedModules')}:</strong> <Badge bg="danger">{resultData.execution_summary.failed_modules || 0}</Badge>
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>{t('workflow:status.totalCost')}:</strong> ${(resultData.execution_summary.total_cost || 0).toFixed(6)}
                                                    </div>
                                                    <div className="col-12">
                                                        <strong>{t('workflow:status.totalExecutionTime')}:</strong> {(resultData.execution_summary.total_execution_time || 0).toFixed(2)} {t('workflow:status.seconds')}
                                                    </div>
                                                </div>
                                            </Alert>
                                        )}

                                        {/* Legacy Summary Information */}
                                        {resultData.summary && !resultData.execution_summary && (
                                            <Alert variant="info" className="mb-3">
                                                <Alert.Heading>{t('workflow:status.summary')}</Alert.Heading>
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <strong>{t('workflow:status.totalProcessed')}</strong> {resultData.summary.total_processed || 0}
                                                    </div>
                                                    <div className="col-md-6">
                                                        <strong>{t('workflow:status.successRate')}</strong> {resultData.summary.success_rate || 'N/A'}
                                                    </div>
                                                </div>
                                                {resultData.summary.processing_time && (
                                                    <div className="mt-2">
                                                        <strong>{t('workflow:status.processingTime')}</strong> {resultData.summary.processing_time}
                                                    </div>
                                                )}
                                            </Alert>
                                        )}

                                        {/* Additional Result Info if no detailed results but has basic info */}
                                        {!resultData.results && !resultData.translation && !resultData.execution_summary && !resultData.summary && (
                                            <Alert variant="warning" className="mb-3">
                                                <Alert.Heading>{t('workflow:status.resultInfo')}</Alert.Heading>
                                                <p>{t('workflow:status.workflowCompleteNoData')}</p>
                                                <p><strong>{t('workflow:status.jobId')}:</strong> {jobId}</p>
                                                <p><strong>{t('workflow:status.status')}:</strong> {jobStatus}</p>
                                            </Alert>
                                        )}

                                        {/* Results Table */}
                                        {resultData.results && Array.isArray(resultData.results) && (
                                            <div className="table-responsive">
                                                <Table striped bordered hover>
                                                    <thead>
                                                        <tr>
                                                            <th>{t('workflow:status.table.row')}</th>
                                                            <th>{t('workflow:status.table.sourceText')}</th>
                                                            <th>{t('workflow:status.table.targetLanguage')}</th>
                                                            <th>{t('workflow:status.table.translation')}</th>
                                                            <th>{t('workflow:status.table.status')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {resultData.results.map((result, index) => (
                                                            <tr key={index}>
                                                                <td>{result.row_index || index}</td>
                                                                <td style={{ maxWidth: '200px', wordWrap: 'break-word' }}>
                                                                    {result.source_text}
                                                                </td>
                                                                <td>{result.target_language}</td>
                                                                <td style={{ maxWidth: '300px', wordWrap: 'break-word' }}>
                                                                    {result.translation}
                                                                </td>
                                                                <td>
                                                                    <Badge bg={result.status === 'success' ? 'success' : 'danger'}>
                                                                        {result.status}
                                                                    </Badge>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}

                                        {/* Single Result Display (for test mode) */}
                                        {resultData.translation && (
                                            <div>
                                                <h5>Test Result</h5>
                                                <div className="border p-3 rounded">
                                                    <div className="mb-2">
                                                        <strong>Source:</strong> {resultData.source_text}
                                                    </div>
                                                    <div className="mb-2">
                                                        <strong>Translation:</strong> {resultData.translation}
                                                    </div>
                                                    {resultData.target_language && (
                                                        <div className="mb-2">
                                                            <strong>Language:</strong> {resultData.target_language}
                                                        </div>
                                                    )}
                                                    {resultData.processing_time && (
                                                        <div>
                                                            <small className="text-muted">
                                                                Processing time: {resultData.processing_time}ms
                                                            </small>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Error Details */}
                                        {resultData.errors && resultData.errors.length > 0 && (
                                            <Alert variant="warning" className="mt-3">
                                                <Alert.Heading>Errors Encountered</Alert.Heading>
                                                <ul className="mb-0">
                                                    {resultData.errors.map((error, index) => (
                                                        <li key={index}>{error}</li>
                                                    ))}
                                                </ul>
                                            </Alert>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <div className="mb-3">
                                <FaEye size={48} className="text-muted" />
                            </div>
                            <h5 className="text-muted">{t('workflow:status.noResultData')}</h5>
                            <p className="text-muted">
                                {jobId ?
                                    t('workflow:status.workflowMayBeRunning') :
                                    t('workflow:status.executeWorkflowFirst')
                                }
                            </p>
                            {jobId && (
                                <Button
                                    variant="outline-primary"
                                    onClick={fetchWorkflowResults}
                                    className="mt-2"
                                >
                                    <FaEye className="me-2" />
                                    {t('workflow:status.retryFetch')}
                                </Button>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-success" onClick={downloadResults}>
                        <FaDownload className="me-2" />
                        {t('workflow:status.downloadResults')}
                    </Button>
                    <Button variant="secondary" onClick={() => setShowTestResult(false)}>
                        {t('common:actions.close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default WorkflowStatusPanel;
